/**
 * Serviço de whitelabel (marca única, global).
 *
 * Carrega a configuração de marca do backend (endpoint público `/marca`) e a
 * aplica em runtime:
 *   - injeta as CSS custom properties do tema (`--vz-*` / `--brand-*`) num
 *     <style id="brand-vars"> (light + dark);
 *   - troca o título do sistema, o favicon, o <link rel="manifest"> e a
 *     <meta theme-color>.
 *
 * As imagens (logo/favicon/ícones) NÃO trafegam no payload de boot: o backend
 * expõe só flags `tem_*` e serve os binários em `/marca/asset/:chave`. Quando
 * uma flag é false, o frontend usa o asset embutido no bundle (marca padrão).
 *
 * Aplica primeiro o cache local (sem flash da marca padrão) e depois atualiza
 * com o dado fresco do servidor. Ver `backend/src/app/controllers/MarcaController.js`.
 */

import { reactive } from 'vue';
import axios from 'axios';
import { api } from '@/helpers/common/api';
import appConfig from '../../app.config.json';

const CACHE_KEY = 'marca_cache_v1';

const PADRAO = {
  nome_sistema: 'PCdoB CRM',
  nome_curto: 'PCdoB',
  nome_exibicao: 'PC do B',
  seo_descricao: 'Sistema Gerencial PC do B',
  twitter_site: '',
  cor_primaria: '#971219',
  cor_primaria_dark: '#3f060a',
  cor_acento: '#fff100',
  cor_acento_dark: '#f2c94c',
  tem_logo_horizontal: false,
  tem_logo_icone: false,
  tem_favicon: false,
  tem_pwa_icon_192: false,
  tem_pwa_icon_512: false,
  tem_og_imagem: false,
  updated_at: null,
};

export const marcaState = reactive({ ...PADRAO });

/**
 * Status de carregamento da marca (separado de `marcaState` de propósito: não é
 * dado da marca e não deve ir pro cache). `carregada` vira true quando a marca
 * fresca do backend foi aplicada (ou a tentativa terminou/expirou). A tela de
 * login usa isso pra exibir um loading e esconder o layout até a marca estar
 * pronta — evitando o flash da marca padrão antes da whitelabel entrar.
 */
export const marcaStatus = reactive({ carregada: false });

/** Converte '#rrggbb' (ou '#rgb') em 'r, g, b' para uso em --vz-primary-rgb. */
function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full.slice(0, 6), 16);
  if (Number.isNaN(n)) return '151, 18, 25';
  /* eslint-disable no-bitwise */
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
  /* eslint-enable no-bitwise */
}

function rgbTriplet(hex) {
  return hexToRgb(hex).split(',').map((x) => parseInt(x, 10));
}

/** rgba(hex, alpha) — mantém os mesmos alfas do tema (texto sobre as barras). */
function rgba(hex, alpha) {
  return `rgba(${hexToRgb(hex)}, ${alpha})`;
}

/** Escurece um hex em direção ao preto por `percent` (0–100). Hover/bordas. */
function shade(hex, percent) {
  const f = 1 - percent / 100;
  const toHex = (x) => Math.max(0, Math.min(255, Math.round(x * f))).toString(16).padStart(2, '0');
  const [r, g, b] = rgbTriplet(hex);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Clareia um hex em direção ao branco por `percent` (0–100). Fundos "subtle". */
function tint(hex, percent) {
  const f = percent / 100;
  const toHex = (x) => Math.max(0, Math.min(255, Math.round(x + (255 - x) * f))).toString(16).padStart(2, '0');
  const [r, g, b] = rgbTriplet(hex);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Cor de texto legível (preto/branco) sobre um fundo — p/ texto do botão. */
function contraste(hex) {
  const [r, g, b] = rgbTriplet(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#212529' : '#ffffff';
}

function assetBase() {
  return String(axios.defaults.baseURL || '').replace(/\/+$/, '');
}

/** URL absoluta (versionada) de uma imagem servida pelo backend. */
export function assetUrl(chave) {
  const v = marcaState.updated_at ? new Date(marcaState.updated_at).getTime() : 0;
  return `${assetBase()}/marca/asset/${chave}?v=${v}`;
}

function injetarCssVars(m) {
  // !important nas vars de superfície: o tema define header/sidebar dentro de
  // seletores [data-topbar="dark"] / [data-sidebar="dark"] (o app roda sempre
  // com esses atributos), que têm especificidade maior que um `:root` simples.
  // O !important garante que a marca vença independente do atributo/modo.
  const p = m.cor_primaria;
  const pd = m.cor_primaria_dark;
  const a = m.cor_acento;
  const ad = m.cor_acento_dark;
  const pHover = shade(p, 15); // hover/active do botão (mesmo % do tema)
  const pText = contraste(p); // texto do botão legível sobre a primária
  const css = `
:root {
  --brand-primary: ${p};
  --brand-primary-dark: ${pd};
  --brand-primary-hover: ${pHover};
  --brand-accent: ${a};
  --brand-accent-dark: ${ad};
  --vz-primary: ${p} !important;
  --vz-primary-rgb: ${hexToRgb(p)} !important;
  --vz-yellow: ${a} !important;
  /* Topbar */
  --vz-header-bg: ${p} !important;
  --vz-header-border: ${shade(p, 8)} !important;
  --vz-header-item-color: ${a} !important;
  --vz-header-item-sub-color: ${a} !important;
  /* Sidebar */
  --vz-vertical-menu-bg: ${p} !important;
  --vz-vertical-menu-border: ${shade(p, 8)} !important;
  --vz-vertical-menu-item-color: ${rgba(a, 0.88)} !important;
  --vz-vertical-menu-item-hover-color: ${a} !important;
  --vz-vertical-menu-item-active-color: ${a} !important;
  --vz-vertical-menu-sub-item-color: ${rgba(a, 0.72)} !important;
  --vz-vertical-menu-sub-item-hover-color: ${a} !important;
  --vz-vertical-menu-sub-item-active-color: ${a} !important;
  --vz-vertical-menu-title-color: ${rgba(a, 0.76)} !important;
  /* Footer */
  --vz-footer-bg: ${p} !important;
  --vz-footer-color: ${rgba(a, 0.88)} !important;
  /* Família "subtle" do Bootstrap (item ativo, badges/alerts primary-subtle). */
  --vz-primary-bg-subtle: ${tint(p, 84)} !important;
  --vz-primary-border-subtle: ${tint(p, 64)} !important;
  --vz-primary-text-emphasis: ${shade(p, 40)} !important;
}
:root[data-bs-theme="dark"] {
  --vz-yellow: ${ad} !important;
  --vz-header-bg: ${pd} !important;
  --vz-header-border: ${shade(pd, 8)} !important;
  --vz-header-item-color: ${ad} !important;
  --vz-header-item-sub-color: ${ad} !important;
  --vz-vertical-menu-bg: ${pd} !important;
  --vz-vertical-menu-border: ${shade(pd, 8)} !important;
  --vz-vertical-menu-item-color: ${rgba(ad, 0.88)} !important;
  --vz-vertical-menu-item-hover-color: ${ad} !important;
  --vz-vertical-menu-item-active-color: ${ad} !important;
  --vz-vertical-menu-sub-item-color: ${rgba(ad, 0.72)} !important;
  --vz-vertical-menu-sub-item-hover-color: ${ad} !important;
  --vz-vertical-menu-sub-item-active-color: ${ad} !important;
  --vz-vertical-menu-title-color: ${rgba(ad, 0.76)} !important;
  --vz-footer-bg: ${pd} !important;
  --vz-footer-color: ${rgba(ad, 0.88)} !important;
  --vz-primary-bg-subtle: rgba(${hexToRgb(p)}, 0.16) !important;
  --vz-primary-border-subtle: rgba(${hexToRgb(p)}, 0.35) !important;
  --vz-primary-text-emphasis: ${tint(p, 40)} !important;
}
.btn-primary {
  --vz-btn-bg: ${p} !important;
  --vz-btn-border-color: ${p} !important;
  --vz-btn-color: ${pText} !important;
  --vz-btn-hover-bg: ${pHover} !important;
  --vz-btn-hover-border-color: ${pHover} !important;
  --vz-btn-hover-color: ${pText} !important;
  --vz-btn-active-bg: ${pHover} !important;
  --vz-btn-active-border-color: ${pHover} !important;
  --vz-btn-active-color: ${pText} !important;
  --vz-btn-disabled-bg: ${p} !important;
  --vz-btn-disabled-border-color: ${p} !important;
  --vz-btn-disabled-color: ${pText} !important;
}
.pagination {
  --vz-pagination-active-bg: ${p} !important;
  --vz-pagination-active-border-color: ${p} !important;
  --vz-pagination-color: ${p} !important;
  --vz-pagination-hover-color: ${pHover} !important;
}
.nav-pills {
  --vz-nav-pills-link-active-bg: ${p} !important;
  --vz-nav-pills-link-active-color: ${pText} !important;
}`;
  let tag = document.getElementById('brand-vars');
  if (!tag) {
    tag = document.createElement('style');
    tag.id = 'brand-vars';
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

/** Fonte do favicon: favicon custom → ícone → logo horizontal → padrão (null). */
function faviconSource(m) {
  if (m.tem_favicon) return assetUrl('favicon');
  if (m.tem_logo_icone) return assetUrl('logo_icone');
  if (m.tem_logo_horizontal) return assetUrl('logo_horizontal');
  return null; // mantém o favicon estático do index.html
}

/** Há algum ícone quadrado da marca no banco (pro apple-touch-icon do iOS)? */
function temIconeMarca(m) {
  return m.tem_pwa_icon_192 || m.tem_logo_icone || m.tem_pwa_icon_512 || m.tem_logo_horizontal;
}

function aplicarFavicon(m) {
  // Favicon da aba: usa favicon custom > ícone > logo horizontal, se houver.
  const src = faviconSource(m);
  if (src) {
    document
      .querySelectorAll('link[rel~="icon"]')
      .forEach((l) => l.parentNode && l.parentNode.removeChild(l));
    const icon = document.createElement('link');
    icon.rel = 'icon';
    icon.href = src;
    document.head.appendChild(icon);
  }

  // apple-touch-icon (ícone da home no iOS): aponta pro endpoint do melhor ícone
  // do banco. iOS moderno lê o DOM ao "Adicionar à tela de início". Só reaponta
  // se houver ícone custom — senão mantém o estático do index.html.
  if (temIconeMarca(m)) {
    const v = m.updated_at ? new Date(m.updated_at).getTime() : 0;
    document
      .querySelectorAll('link[rel="apple-touch-icon"]')
      .forEach((l) => l.parentNode && l.parentNode.removeChild(l));
    const apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = `${assetBase()}/marca/appleicon?v=${v}`;
    document.head.appendChild(apple);
  }
}

function aplicarManifest() {
  const href = `${assetBase()}/marca/manifest`;
  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  link.href = href;
}

function aplicarThemeColor(m) {
  const dark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
  const cor = dark ? m.cor_primaria_dark : m.cor_primaria;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', cor);
}

function aplicarTitulo(m) {
  // appConfig.title é lido pelo router a cada navegação (mutação no singleton
  // importado surte efeito nas próximas navegações). Atualiza também o título
  // atual pra refletir de imediato.
  appConfig.title = m.nome_sistema;
  document.title = m.nome_sistema;
}

/** Cria/atualiza uma <meta> por name=... ou property=.... */
function upsertMeta(attr, key, content) {
  if (content == null || content === '') return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * SEO + Open Graph (Facebook) + Twitter Card. Título/site_name derivam dos
 * nomes da marca; a imagem usa og_imagem (ou a logo horizontal como fallback).
 */
function aplicarSeo(m) {
  const titulo = m.nome_sistema || 'PCdoB CRM';
  const desc = m.seo_descricao || '';
  const siteName = m.nome_exibicao || m.nome_curto || titulo;
  const url = `${window.location.origin}/`;
  let imagem = null;
  if (m.tem_og_imagem) imagem = assetUrl('og_imagem');
  else if (m.tem_logo_horizontal) imagem = assetUrl('logo_horizontal');
  const card = imagem ? 'summary_large_image' : 'summary';

  if (desc) upsertMeta('name', 'description', desc);

  // Open Graph
  upsertMeta('property', 'og:title', titulo);
  if (desc) upsertMeta('property', 'og:description', desc);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:site_name', siteName);
  upsertMeta('property', 'og:url', url);
  if (imagem) upsertMeta('property', 'og:image', imagem);

  // Twitter Card
  upsertMeta('name', 'twitter:card', card);
  upsertMeta('name', 'twitter:title', titulo);
  if (desc) upsertMeta('name', 'twitter:description', desc);
  if (imagem) upsertMeta('name', 'twitter:image', imagem);
  if (m.twitter_site) upsertMeta('name', 'twitter:site', m.twitter_site);
}

/** Aplica um objeto de marca no state + DOM. */
export function aplicarMarca(m) {
  Object.assign(marcaState, m);
  injetarCssVars(marcaState);
  aplicarTitulo(marcaState);
  aplicarThemeColor(marcaState);
  aplicarFavicon(marcaState);
  aplicarManifest();
  aplicarSeo(marcaState);
}

/**
 * Carrega a marca: aplica o cache imediatamente (evita flash) e busca a versão
 * fresca do backend em seguida. Idempotente — pode ser chamado de novo após um
 * salvamento no admin para re-aplicar.
 */
export async function carregarMarca() {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    aplicarMarca(cache && typeof cache === 'object' ? { ...PADRAO, ...cache } : PADRAO);
  } catch (_e) {
    aplicarMarca(PADRAO);
  }

  // Rede de segurança: se o /marca travar (sem timeout próprio), libera a UI
  // mesmo assim depois de 6s — o loading do login não pode prender o acesso.
  const liberarPorTimeout = setTimeout(() => { marcaStatus.carregada = true; }, 6000);

  try {
    const data = await api.read('/marca');
    if (data && typeof data === 'object') {
      aplicarMarca({ ...PADRAO, ...data });
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ...marcaState }));
    }
  } catch (_e) { /* mantém cache/padrão em caso de falha */ }

  clearTimeout(liberarPorTimeout);
  marcaStatus.carregada = true;
  return marcaState;
}
